import { FC, useCallback, useMemo } from 'react'

import { getFormattedFinance } from 'cng-common/helpers/numbers'

interface ICurrencyValueProps {
  value: number | null
  auto?: boolean
  divider?: number
  suffix?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  withCurrencySign?: boolean
}

export const CurrencyValue: FC<ICurrencyValueProps> = ({
  value,
  auto = false,
  divider = 1000000,
  suffix = 'M',
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  withCurrencySign = true,
}) => {
  const resolveAutoFormat = useCallback((valueToFormat: number) => {
    const absoluteValue = Math.abs(valueToFormat)

    if (absoluteValue < 1000) {
      return { divider: 1, suffix: '', maximumFractionDigits: 1 }
    }

    if (absoluteValue < 1000000) {
      return { divider: 1000, suffix: 'K', maximumFractionDigits: 2 }
    }

    return { divider: 1000000, suffix: 'M', maximumFractionDigits: 2 }
  }, [])

  const formattedValue = useMemo(() => {
    if (value === null || value === undefined) {
      return '-'
    }

    const resolved = auto ? resolveAutoFormat(value) : null
    const effectiveDivider = resolved?.divider ?? divider
    const effectiveSuffix = resolved?.suffix ?? suffix
    const effectiveMaximumFractionDigits = resolved?.maximumFractionDigits ?? maximumFractionDigits

    const normalizedValue = effectiveDivider ? value / effectiveDivider : value
    const formatted = getFormattedFinance(normalizedValue, {
      withCurrencySign,
      minimumFractionDigits,
      maximumFractionDigits: effectiveMaximumFractionDigits,
    })

    return `${formatted}${effectiveSuffix}`
  }, [
    auto,
    divider,
    maximumFractionDigits,
    minimumFractionDigits,
    resolveAutoFormat,
    suffix,
    value,
    withCurrencySign,
  ])

  return <span>{formattedValue}</span>
}
